import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { UseFormReturn } from 'react-hook-form'
import { z } from 'zod'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog } from '@/features/dialogs/common/types'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { getBorrowMaxValue } from '@/features/sparklend/common/utils/action-max-value-getters/getBorrowMaxValue'
import {
  borrowValidationIssueToMessage,
  getValidateBorrowArgs,
  validateBorrow,
} from '@/features/sparklend/common/validation/validateBorrow'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getBorrowDialogFormValidator(marketInfo: MarketInfo, reserve: Reserve) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const value = NormalizedNumber(field.value === '' ? '0' : field.value)

    const validationIssue = validateBorrow(getValidateBorrowArgs(value, reserve, marketInfo))

    if (validationIssue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: borrowValidationIssueToMessage[validationIssue],
        path: ['value'],
      })
    }
  })
}

export interface GetFormFieldsForBorrowDialogParams {
  form: UseFormReturn<AssetInputSchema>
  reserve: Reserve
  marketInfo: MarketInfo
  marketInfoIn1Epoch: MarketInfo
  walletInfo: MarketWalletInfo
}
export function getFormFieldsForBorrowDialog({
  form,
  reserve,
  marketInfo,
  marketInfoIn1Epoch,
  walletInfo,
}: GetFormFieldsForBorrowDialogParams): FormFieldsForDialog {
  // eslint-disable-next-line func-style
  const changeAsset = (newSymbol: TokenSymbol): void => {
    form.setValue('symbol', newSymbol)
    form.setValue('value', '')
    form.setValue('isMaxSelected', false)
    form.clearErrors()
  }

  const { symbol, value } = form.getValues()
  const selectedToken = marketInfo.findOneBySymbol(symbol)
  const selectedTokenBalance = walletInfo.findWalletBalanceForSymbol(symbol)
  // We use the reserve from the future market info to account for possible interest accrual in the reserve.
  // This is important for the borrow cap and isolated debt ceiling validation.
  // In the same time we use current market info to get the user's current position - we do not
  // account for interest accrual here.
  const reserveIn1Epoch = marketInfoIn1Epoch.findOneReserveBySymbol(reserve.token.symbol)

  const borrowValidationArgs = getValidateBorrowArgs(NormalizedNumber.ZERO, reserveIn1Epoch, marketInfo)
  const validationIssue = validateBorrow(borrowValidationArgs)
  const borrowMaxValue = getBorrowMaxValue({
    validationIssue,
    user: borrowValidationArgs.user,
    asset: borrowValidationArgs.asset,
  })

  return {
    selectedAsset: selectedToken,
    selectedAssetBalance: selectedTokenBalance,
    value,
    maxSelectedFieldName: 'isMaxSelected',
    changeAsset,
    maxValue: borrowMaxValue,
  }
}
